import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, KeyRound, MailCheck } from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";


export function ForgotPasswordPage() {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email) return toast.error("Email required");
        setSubmitting(true);
        try {
            await api.post("/auth/password/reset/request", { email });
            setSent(true);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Layout title="Forgot password" icon={<KeyRound size={14} className="gold-text" strokeWidth={1.5} />}>
            {sent ? (
                <div className="text-center" data-testid="reset-sent">
                    <MailCheck size={32} className="gold-text mx-auto mb-4" strokeWidth={1.2} />
                    <p className="text-sm text-foreground mb-2">Check your inbox.</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                        If <span className="gold-text">{email}</span> is registered, we've sent a reset link valid for 1 hour.
                    </p>
                    <Link
                        to="/auth?mode=login"
                        className="inline-block mt-6 text-[10px] uppercase tracking-[0.2em] gold-text hover:opacity-80"
                        data-testid="back-to-signin"
                    >
                        ← Back to sign in
                    </Link>
                </div>
            ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="forgot-password-form">
                    <p className="text-xs text-muted-foreground leading-relaxed mb-2">
                        Enter the email tied to your account. We'll send you a link to set a new password.
                    </p>
                    <div>
                        <Label className="overline mb-2 block text-[10px]">Email</Label>
                        <Input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@email.com"
                            className="bg-input border-white/10 h-11 rounded-sm"
                            data-testid="forgot-email-input"
                        />
                    </div>
                    <Button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs h-11"
                        data-testid="forgot-submit-btn"
                    >
                        {submitting ? "Sending…" : <>Send reset link <ArrowRight size={14} className="ml-2" /></>}
                    </Button>
                    <Link to="/auth?mode=login" className="block text-center text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors mt-4">
                        ← Back to sign in
                    </Link>
                </form>
            )}
        </Layout>
    );
}


export function ResetPasswordPage() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!token) toast.error("Reset token missing — request a new link");
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (pw.length < 6) return toast.error("Password must be at least 6 characters");
        if (pw !== pw2) return toast.error("Passwords don't match");
        setSubmitting(true);
        try {
            await api.post("/auth/password/reset/confirm", { token, new_password: pw });
            toast.success("Password updated. Sign in with your new password.");
            navigate("/auth?mode=login");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Layout title="Set new password" icon={<KeyRound size={14} className="gold-text" strokeWidth={1.5} />}>
            <form onSubmit={submit} className="space-y-5" data-testid="reset-password-form">
                <div>
                    <Label className="overline mb-2 block text-[10px]">New password</Label>
                    <Input
                        type="password"
                        value={pw}
                        onChange={(e) => setPw(e.target.value)}
                        placeholder="Min 6 characters"
                        className="bg-input border-white/10 h-11 rounded-sm"
                        data-testid="reset-pw-input"
                    />
                </div>
                <div>
                    <Label className="overline mb-2 block text-[10px]">Confirm password</Label>
                    <Input
                        type="password"
                        value={pw2}
                        onChange={(e) => setPw2(e.target.value)}
                        className="bg-input border-white/10 h-11 rounded-sm"
                        data-testid="reset-pw2-input"
                    />
                </div>
                <Button
                    type="submit"
                    disabled={submitting || !token}
                    className="w-full bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs h-11"
                    data-testid="reset-submit-btn"
                >
                    {submitting ? "Saving…" : "Update password"}
                </Button>
            </form>
        </Layout>
    );
}


export function VerifyEmailPage() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [status, setStatus] = useState("verifying"); // verifying | ok | err

    useEffect(() => {
        if (!token) {
            setStatus("err");
            return;
        }
        api.post("/auth/verify-email/confirm", { token })
            .then(() => setStatus("ok"))
            .catch(() => setStatus("err"));
    }, [token]);

    return (
        <Layout title="Email verification" icon={<MailCheck size={14} className="gold-text" strokeWidth={1.5} />}>
            <div className="text-center py-6" data-testid={`verify-${status}`}>
                {status === "verifying" && <p className="text-sm text-muted-foreground">Verifying your email…</p>}
                {status === "ok" && (
                    <>
                        <MailCheck size={32} className="gold-text mx-auto mb-4" strokeWidth={1.2} />
                        <p className="text-sm text-foreground mb-2">Email verified.</p>
                        <p className="text-xs text-muted-foreground">Thank you — your account is fully set up.</p>
                    </>
                )}
                {status === "err" && (
                    <>
                        <p className="text-sm text-foreground mb-2">Verification link invalid or expired.</p>
                        <p className="text-xs text-muted-foreground">Please request a new one from your profile.</p>
                    </>
                )}
                <Link to="/home" className="inline-block mt-6 text-[10px] uppercase tracking-[0.2em] gold-text hover:opacity-80">
                    Continue →
                </Link>
            </div>
        </Layout>
    );
}


function Layout({ title, icon, children }) {
    return (
        <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-background">
            <div className="w-full max-w-md">
                <Link to="/home" className="block text-center mb-8 text-[10px] uppercase tracking-[0.4em] gold-text">
                    SMART Jamaica
                </Link>
                <div className="bg-card border border-white/10 rounded-sm p-8 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)]">
                    <div className="flex items-center gap-2 mb-6">
                        {icon}
                        <h1 className="font-serif text-2xl">{title}</h1>
                    </div>
                    {children}
                </div>
            </div>
        </div>
    );
}
